import React, { useCallback, useState, useMemo } from 'react';
import classNames from 'classnames';
import { MultiStackedBarChart, IChartProps } from '@fluentui/react-charting';
import { TooltipHost } from '@fluentui/react';
import { Glyphicon } from '@bingads-webui-react/primitive-unthemed';
import { Dropdown } from '@fluentui/react/lib/Dropdown';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { formatMetric, formatMetricWithSign, formatChangePercentageWithSign } from '@bingads-webui-campaign-legacy/performance-metric-formatter/index';
import { computerColor, tabletColor, mobileColor, connectedTVColor } from '../constants/device-color';
import { DeviceComparisonPatterns } from '../helpers/svg-pattern-comparison-state';
import { legendKeyMap } from '../utils/legend-key-map';
import { DeviceCardProps } from '../utils/types';
import { getSegmentedData } from '../utils/segment-data-extractor';
import { LegendIcon } from '../utils/callout-legend-icon';
import { Metric } from '../utils/metric-formatter';
import { calculatePercentageOfTotal } from '../utils/total-percentage-calculator';
import { metrics } from '../constants/metrics-data';
import { dropdownCustomStyles } from '../styles/dropdown-styles';
import { getStyles } from '../styles/devices-comparison-card';

export function DeviceComparisonCard({
  i18n, deviceData, dateRange, allMetrics, initialSelectedMetrics, patchPreference, hideCurrency, footerVisible,
}: DeviceCardProps) {
  const currentComparisonData = useMemo(() => getSegmentedData(deviceData, ['PerformanceMetrics', 'SegmentedData']), [deviceData]);

  const previousComparisonData = useMemo(() => getSegmentedData(deviceData, ['PeriodPerformanceMetrics', 'SegmentedData']), [deviceData]);

  const changeData = useMemo(() => {
    if (!currentComparisonData.length || !previousComparisonData.length) return [];

    const previousDataMap = previousComparisonData.reduce((acc, item) => {
      const deviceIdentifier = item.deviceType || 'defaultDevice';
      acc[deviceIdentifier] = item;
      return acc;
    }, {});

    return currentComparisonData.map((current) => {
      const deviceIdentifier = current.deviceType || 'defaultDevice';

      const previous = previousDataMap[deviceIdentifier] || {};

      return Object.keys(current).reduce((acc, key) => {
        acc[key] =
          typeof current[key] === 'number' && typeof previous[key] === 'number'
            ? current[key] - previous[key]
            : current[key];

        return acc;
      }, { deviceIdentifier });
    });
  }, [currentComparisonData, previousComparisonData]);

  const changeDataPercentage = useMemo(() => getSegmentedData(deviceData, ['PerformanceMetricsChangePercentage', 'SegmentedData']), [deviceData]);
  const currentDateRange = dateRange ? `${dateRange.RangeStartDate} - ${dateRange.RangeEndDate}` : '';
  const previousDateRange = dateRange && dateRange.compared ? `${dateRange.compared.formattedStart} - ${dateRange.compared.formattedEnd}` : '';

  const currentPeriodTotals = deviceData?.OverallTotals?.PerformanceMetrics || deviceData?.PerformanceMetrics;
  const previousPeriodTotals = deviceData?.OverallTotals?.PeriodPerformanceMetrics || deviceData?.PeriodPerformanceMetrics;
  const styles = useDefaultStyles(() => getStyles(footerVisible));

  const createChartSeriesData = useCallback(
    (deviceMetrics, metric, dateRange, isPattern = false) => {
      /**
       * Maps device types to their respective colors or patterns.
       * This mapping is used to set the colors or patterns for chart bars based on current or previous date range.
       *
       * @param isPattern - A boolean indicating whether to use a pattern (true) or solid color (false).
       * @returns An object where each device type is associated with either a pattern URL or a solid color.
       */
      const colorMap = {
        Computer: isPattern ? 'url(#pattern1)' : computerColor,
        Tablet: isPattern ? 'url(#pattern2)' : tabletColor,
        SmartPhone: isPattern ? 'url(#pattern3)' : mobileColor,
        ConnectedTV: isPattern ? 'url(#pattern4)' : connectedTVColor,
      };

      const validDevices = ['Computer', 'Tablet', 'SmartPhone', 'ConnectedTV'];

      return validDevices
        .filter(deviceType => deviceMetrics[deviceType])
        .map((deviceType) => {
          const value = deviceMetrics[deviceType][metric];
          const formattedValue = formatMetric(metric, value);

          return {
            legend: deviceType,
            data: value,
            color: colorMap[deviceType],
            legendColor: colorMap[deviceType],
            metric,
            dateRange,
            metricValue: formattedValue,
          };
        });
    },
    []
  );

  const prepareDeviceData = useCallback(
    comparisonData => comparisonData.reduce((deviceDataAccumulator, { deviceType, ...metrics }) => ({
      ...deviceDataAccumulator, [deviceType]: metrics,
    }), {}),
    []
  );

  const createChartPoints = useCallback(
    (metric: Metric): IChartProps[] => {
      const currentDeviceData = prepareDeviceData(currentComparisonData);
      const previousDeviceData = prepareDeviceData(previousComparisonData);

      return [
        {
          chartTitle: '',
          chartData: createChartSeriesData(currentDeviceData, metric, currentDateRange),
        },
        {
          chartTitle: '',
          chartData: createChartSeriesData(previousDeviceData, metric, previousDateRange, true),
        },
      ];
    },
    [prepareDeviceData, currentComparisonData, previousComparisonData, createChartSeriesData, currentDateRange, previousDateRange]
  );

  const chartPoints: Record<Metric, IChartProps[]> = useMemo(
    () =>
      metrics.reduce((accumulator, metric) => ({
        ...accumulator,
        [metric]: createChartPoints(metric),
      }), {} as Record<Metric, IChartProps[]>),
    [createChartPoints]
  );

  const getCombinedDataPoints = useCallback((data) => {
    const combined = {};

    if (Array.isArray(data)) {
      data.forEach((entry) => {
        if (entry.chartData) {
          entry.chartData.forEach((dataPoint) => {
            const { legend } = dataPoint;
            if (!combined[legend]) {
              combined[legend] = [];
            }
            combined[legend].push(dataPoint);
          });
        }
      });
    }

    return combined;
  }, []);

  const calloutStyles = useMemo(() => ({
    customCallout: styles.customCallout,
    dataItem: styles.dataItem,
    gridContainerStyle: styles.gridContainerStyle,
    separator: styles.separator,
    leftGrid: styles.leftGrid,
    rightGrid: styles.rightGrid,
    legend: styles.legend,
  }), [styles]);

  const renderCustomCallout = useCallback((point, combinedData) => {
    if (!point || !point.legend) return null;

    // Map legend to its updated version if needed
    const mappedLegend = legendKeyMap[point.legend] || point.legend;

    const changeDataEntry = changeData.find(entry => entry.deviceIdentifier === point.legend);
    const change = changeDataEntry ? changeDataEntry[point.metric] : null;
    let roundedValue: number = 0;

    if (change !== null) {
      const numericChange = Number(change);
      if (Number.isFinite(numericChange)) {
        roundedValue = numericChange;
      }
    }

    const changeDataPercentageEntry = changeDataPercentage.find(entry => entry.deviceType === point.legend);
    const changePercentage = changeDataPercentageEntry?.[point.metric] ?? null;
    let roundedValuePercentage = '0.00%';

    if (changePercentage !== null) {
      const numericChangePercentage = Number(changePercentage);

      if (Number.isFinite(numericChangePercentage)) {
        if (numericChangePercentage === 0) {
          roundedValuePercentage = '0.00%';
        } else if (numericChangePercentage > 1e10) {
          roundedValuePercentage = '+∞';
        } else if (numericChangePercentage < -1e10) {
          roundedValuePercentage = '-∞';
        } else {
          roundedValuePercentage = formatChangePercentageWithSign(numericChangePercentage);
        }
      }
    }

    const legendData = Array.isArray(combinedData[point.legend])
      ? combinedData[point.legend]
      : [combinedData[point.legend]];

    const metricDisplayName = allMetrics.find(metric => metric.key === point.metric)?.displayName || point.metric;

    const metricsToHidePercentage = new Set([
      'CTR',
      'Top impr. rate',
      'Abs. top impr. rate',
      'Conv. rate',
      'Return on ad spend',
    ]);
    const hidePercentageOfTotal = metricsToHidePercentage.has(metricDisplayName);

    const metricValue = legendData[0]?.metricValue || '0';
    const totalMetric = currentPeriodTotals?.[point.metric] ?? 0;

    const currentPercentageOfTotalMetric = calculatePercentageOfTotal(metricValue, totalMetric);

    const previousMetricValue = legendData[1]?.metricValue ?? '0';
    const previousTotalMetric = previousPeriodTotals?.[point.metric] ?? 1;

    const previousPercentageOfTotalMetric = calculatePercentageOfTotal(previousMetricValue, previousTotalMetric);
    const renderMetricValue = (metricValue, percentageMetricKey, percentageMetricValue) => (
      <b>
        {metricValue}
        {!hidePercentageOfTotal &&
          i18n.getString(_TL_(' ({{percentage}}% of total)'), {
            percentage: percentageMetricValue,
          })
        }
      </b>
    );

    const changeValue = formatMetricWithSign(allMetrics.find(metric => metric.key === point.metric).key, roundedValue);

    return (
      <div className={classNames(calloutStyles.customCallout, 'ba-device-comparison-callout')}>
        {legendData.length > 0 && (
        <>
          <div className={classNames('ba-device-comparison-callout-title')}>{i18n.getString(mappedLegend)}</div>
          <div className={classNames('ba-device-comparison-callout-metric-name')}>{metricDisplayName}</div>
          <div className={calloutStyles.gridContainerStyle}>
            <div className={calloutStyles.dataItem}>
              {legendData[0] && (
              <>
                <div className={classNames('ba-device-comparison-callout-legend-icon-current')}>
                  <LegendIcon color={legendData[0].legendColor} />
                </div>
                <div className={styles.legend}>{legendData[0].dateRange}</div>
              </>
              )}
            </div>
            <div className={calloutStyles.separator} />
            <div className={calloutStyles.dataItem}>
              {legendData[1] && (
              <>
                <div className={classNames('ba-device-comparison-callout-legend-icon-previous')}>
                  <LegendIcon color={legendData[0].legendColor} useGradient />
                </div>
                <div className={styles.legend}>{legendData[1].dateRange}</div>
              </>
              )}
            </div>
            <div className={classNames(calloutStyles.leftGrid, 'ba-device-comparison-callout-current-total-value')}>
              {renderMetricValue(legendData[0]?.metricValue, 'currentPercentageOfTotalMetric', currentPercentageOfTotalMetric)}
            </div>
            <div className={calloutStyles.separator} />
            <div className={classNames(calloutStyles.rightGrid, 'ba-device-comparison-callout-previous-total-value')}>
              {renderMetricValue(legendData[1]?.metricValue, 'previousPercentageOfTotalMetric', previousPercentageOfTotalMetric)}
            </div>
          </div>
          <div>
            {
              i18n.getString(_TL_('Change: {{changeValue}} ({{roundedValuePercentage}})'), {
                changeValue,
                roundedValuePercentage,
              })
            }
          </div>
        </>
          )}
      </div>
    );
  }, [changeData, changeDataPercentage, currentPeriodTotals, previousPeriodTotals, allMetrics, calloutStyles, styles.legend, i18n]);

  const [selectedViews, setSelectedViews] = useState(initialSelectedMetrics.map(metric => ({
    id: metric.key,
    selectedKey: metric.key,
  })));

  const dropdownOptions = useMemo(
    () =>
      selectedViews.map((view, index) => {
        const currentMetric = allMetrics.find(metric => metric.key === view.selectedKey);
        const selectedKeys = selectedViews.map(v => v.selectedKey);

        const options = [
          { key: currentMetric?.key, text: currentMetric?.displayName },
          ...allMetrics
            .filter(metric => metric.key !== view.selectedKey && !selectedKeys.includes(metric.key))
            .map(metric => ({
              key: metric.key,
              text: metric.displayName,
            })),
        ];
        return options;
      }),
    [selectedViews, allMetrics]
  );

  const handleDropdownChange = useCallback(
    (id, option) => {
      const newSelectedViews = selectedViews.map(view => (view.id === id ? { ...view, selectedKey: option.key } : view));
      setSelectedViews(newSelectedViews);
      patchPreference({ selectedColumns: newSelectedViews.map(view => view.selectedKey) });
    },
    [patchPreference, selectedViews]
  );

  return (
    <div className={classNames(styles.deviceComparisonCard, 'ba-device-comparison-card')}>
      {selectedViews.map((view, index) => (
        <div key={`${view.id}-device-comparison-card`}>
          <div className={classNames(`ba-device-comparison-dropdown-${index}`, styles.dropdownComparisonCard)}>
            <Dropdown
              options={dropdownOptions[index]}
              onChange={(e, option) => handleDropdownChange(view.id, option)}
              selectedKey={view.selectedKey}
              styles={dropdownCustomStyles}
              aria-label={i18n.getString(_TL_('Select an option from the dropdown'))}
            />
            {hideCurrency && (view.selectedKey === 'AverageCPC' || view.selectedKey === 'Spend' || view.selectedKey === 'PartialConversionCPA' || view.selectedKey === 'AdvertiserReportedRevenue') && (
            <TooltipHost
              content={i18n.getString(_TL_('Value is not shown because you have accounts with different currencies.'))}
            >
              <Glyphicon glyph="ba-Info" className="metric-info" />
            </TooltipHost>
          )}
          </div>
          <DeviceComparisonPatterns />
          <div className={`${styles.multiStackedBarChart} ${styles.chartTitleHidden}`}>
            <div className={classNames(`ba-device-comparison-bar-chart-${index}`)}>
              <MultiStackedBarChart
                hideLegend
                culture={window.navigator.language}
                data={chartPoints[view.selectedKey]}
                onRenderCalloutPerDataPoint={props => renderCustomCallout(props, getCombinedDataPoints(chartPoints[view.selectedKey]))}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
